/**
 * The rules a statistical alarm's chart raises (#926 review, L3). The server refuses a rule the chart's type never
 * raises (422 spc_rule_not_raised: an alarm on it could never fire), so the rule form offers, ticks and saves only
 * the chart's set: an I-MR or X-bar chart's run rules, an EWMA chart's EWMA rule, a CUSUM chart's two CUSUM rules.
 */
import { useQuery } from "@tanstack/react-query";
import { SPC_KEYS, spcApi, useSpcChartsForTag } from "@/lib/api/spc";
import { spcAlarmCharts, spcAlarmRulesFor, type SpcAlarmRuleName } from "./spc-condition";

/**
 * The rules a rule's chart raises: its type's set, read off the tag's active charts, or off the chart itself when it
 * is not active now (a paused chart); every rule until the chart's type is known.
 */
export function useSpcAlarmOfferedRules(tagId: string, chartId: string): SpcAlarmRuleName[] {
  const list = useSpcChartsForTag(tagId || null);
  const active = spcAlarmCharts(list.data?.charts, tagId).find((c) => c.id === chartId);
  const detail = useQuery({
    queryKey: SPC_KEYS.chart(chartId),
    queryFn: () => spcApi.getChart(chartId),
    enabled: list.isSuccess && chartId !== "" && !active,
    staleTime: 60_000,
  });
  return spcAlarmRulesFor(active?.chart_type ?? detail.data?.chart.chart_type);
}
